import { TONCENTER_API_KEY, TONCENTER_WEBSOCKET_ENDPOINT } from '~/config';
import { buildLiquidityMessages, dropExcludedActions, dropGaslessMarkers, dropGaslessTwins, generateMessage, getIndexedTraceFinality } from '~/api/toncenterV3';
import store from '../store.js';

let ws;
let isOpen = false;
const messageQueue = [];
let reconnectTimeout = null;
let subscriptions = [];
let heartbeatInterval = null;

const FINALITY_RANK = { pending: 0, confirmed: 1, finalized: 2 };

// A row can stay pending for good: its later notifications may have gone out before the page
// subscribed, or while the socket was reconnecting, and the stream replays nothing. Such rows
// are looked up in the index, which only has traces that are done.
const STALE_PENDING_MS = 7000;
const MAX_PENDING_CHECKS = 10;
const pendingChecks = new Map();
let isCheckingPending = false;

function withFinality(tx, finality) {
    return {
        ...tx,
        finality,
        messages: tx.messages.map(msg => ({ ...msg, finality })),
    };
}

async function checkStalePending() {
    if (isCheckingPending) return;

    const now = Date.now();
    const pendingHashes = new Set(store.state.websocketPendingTransactions
        .filter(tx => tx.finality === 'pending')
        .map(tx => tx.hash));

    pendingChecks.forEach((check, hash) => {
        if (!pendingHashes.has(hash)) pendingChecks.delete(hash);
    });

    const stale = [];

    pendingHashes.forEach((hash) => {
        const check = pendingChecks.get(hash);

        if (!check) {
            pendingChecks.set(hash, { since: now, count: 0 });
        } else if (now - check.since >= STALE_PENDING_MS && check.count < MAX_PENDING_CHECKS) {
            stale.push(hash);
        }
    });

    if (!stale.length) return;

    const hashes = stale.slice(0, 10);
    hashes.forEach((hash) => { pendingChecks.get(hash).count += 1; });

    isCheckingPending = true;

    try {
        const finality = await getIndexedTraceFinality(hashes);

        // Updated in place, so the row keeps its position in the list
        store.state.websocketPendingTransactions = store.state.websocketPendingTransactions.map(tx => (
            tx.finality === 'pending' && FINALITY_RANK[finality[tx.hash]] > FINALITY_RANK.pending
                ? withFinality(tx, finality[tx.hash])
                : tx
        ));
    } catch (error) {
        // Not in the index yet or a failed request, either way the next tick asks again
    } finally {
        isCheckingPending = false;
    }
}

function sendWhenReady(data) {
    const payload = JSON.stringify(data);

    if (isOpen) {
        ws.send(payload);
    } else {
        messageQueue.push(payload);
    }
}

function flushQueue() {
    while (messageQueue.length > 0 && isOpen) {
        ws.send(messageQueue.shift());
    }
}

function stopHeartbeat() {
    if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        heartbeatInterval = null;
    }
}

function startHeartbeat() {
    stopHeartbeat(); // на всякий случай сбрасываем старый

    heartbeatInterval = setInterval(() => {
        sendWhenReady({ operation: 'ping' });
        checkStalePending();
    }, 7000); // 7 секунд – оптимально
}

function formatActions(addr, actionsRaw, address_book, metadata, finality) {
    const actions = [];

    dropGaslessMarkers(dropGaslessTwins(dropExcludedActions(actionsRaw)), address_book).forEach((action) => {
        const hash_norm = action.trace_external_hash_norm || action.trace_external_hash;
        const findByHash = actions.findIndex(({ hash }) => hash === hash_norm);

        const msg = generateMessage(action, address_book, addr, metadata, finality);

        msg.hash = hash_norm;

        if (msg.event === 'dex_withdraw_liquidity' || msg.event === 'dex_deposit_liquidity') {
            actions.push({
                address: addr,
                fee: null,
                hash: hash_norm,
                lt: action.end_lt,
                trace_end_lt: action.trace_end_lt,
                timestamp: action.end_utime * 1000,
                messages: buildLiquidityMessages(msg),
                action: 'ok',
                finality,
            });
            return;
        }

        if (findByHash === -1) {
            actions.push({
                address: addr,
                fee: null,
                hash: hash_norm,
                lt: action.end_lt,
                trace_end_lt: action.trace_end_lt,
                timestamp: action.end_utime * 1000,
                messages: [msg],
                action: 'ok',
                finality,
            });
        } else {
            actions[findByHash].messages.push(msg);
        }
    });

    return actions;
}

function establishWebsocketConnection() {
    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
    }

    ws = new WebSocket(`${TONCENTER_WEBSOCKET_ENDPOINT}?api_key=${TONCENTER_API_KEY}`);

    ws.onopen = () => {
        isOpen = true;
        // console.log('connected');

        // restore subscriptions after reconnect
        // eslint-disable-next-line no-restricted-syntax
        for (const sub of subscriptions) {
            sendWhenReady(sub);
        }

        // Make ping request so connection will not close
        startHeartbeat();

        flushQueue();
    };

    ws.onmessage = (msg) => {
        const data = JSON.parse(msg.data);

        // console.log('new action:', data);

        if (data?.type === 'account_state_change') {
            if (data?.finality === 'confirmed' || data?.finality === 'finalized') {
                store.state.websocketAccountState = data?.state;
            }
        } else if (data?.type === 'jettons_change') {
            if (data?.finality === 'confirmed') {
                const assets = store.state.websocketAddressAssets;
                if (assets?.jetton_wallets && assets.jetton_wallets.length > 0) {
                    // case: address has 1 or more assets
                    const jettonData = data.jetton;
                    const index = assets.jetton_wallets.findIndex(item => item.jetton === jettonData.jetton);

                    if (index === -1) {
                        // new jetton
                        store.state.websocketAddressAssets.jetton_wallets.push(jettonData);
                        store.state.websocketAddressAssets.address_book[jettonData.jetton] = data.address_book[jettonData.jetton];
                        store.state.websocketAddressAssets.metadata[jettonData.jetton] = data.metadata[jettonData.jetton];
                    } else {
                        // existing jetton
                        store.state.websocketAddressAssets.jetton_wallets.splice(index, 1, jettonData);
                    }
                } else {
                    // case: address has 0 assets
                    store.state.websocketAddressAssets = {
                        jetton_wallets: [data.jetton],
                        address_book: data.address_book,
                        metadata: data.metadata,
                    };
                }
            }
        } else if (data?.type === 'actions') {
            // if (data?.finality === 'finalized') {
            //     // Skip finalized updates
            //     return;
            // }

            const hash_norm = data?.trace_external_hash_norm || data?.trace_external_hash;
            const current = store.state.websocketPendingTransactions.find(item => item.hash === hash_norm);

            // The stream keeps no order within a trace, so a late pending may come after its finalized
            if (current && FINALITY_RANK[data?.finality] < FINALITY_RANK[current.finality]) return;

            pendingChecks.delete(hash_norm);

            const formattedActions = formatActions(subscriptions[0].addresses[0], data?.actions, data?.address_book, data?.metadata, data?.finality);

            store.state.websocketPendingTransactions = store.state.websocketPendingTransactions
                .filter(item => item.hash !== hash_norm);

            store.state.websocketPendingTransactions.unshift(...formattedActions);
        } else if (data?.type === 'trace_invalidated') {
            const hash_norm = data?.trace_external_hash_norm || data?.trace_external_hash;

            store.state.websocketPendingTransactions = store.state.websocketPendingTransactions
                .filter(item => item.hash !== hash_norm);

            store.state.websocketTransactions = store.state.websocketTransactions
                .filter(item => item.hash !== hash_norm);
        }
    };

    ws.onerror = (err) => {
        console.error('ws error', err);
        stopHeartbeat();
    };

    ws.onclose = () => {
        isOpen = false;
        // console.log('ws closed, reconnecting...');
        stopHeartbeat();

        reconnectTimeout = setTimeout(() => {
            establishWebsocketConnection();
        }, 1000);
    };
}

export function startTxAndStateWebsocket(address) {
    const params = {
        id: address,
        operation: 'subscribe',
        addresses: [address],
        types: ['actions', 'account_state_change', 'jettons_change'],
        min_finality: 'pending',
        include_address_book: true,
        include_metadata: true,
    };

    subscriptions.push(params);

    if (!ws || ws.readyState === WebSocket.CLOSED) {
        establishWebsocketConnection();
    } else {
        sendWhenReady(params);
    }
}

export function stopTxAndStateWebsockets() {
    if (!ws || ws.readyState === WebSocket.CLOSED) return;

    const addresses = subscriptions.map(subscription => subscription.addresses[0]);

    const params = {
        operation: 'unsubscribe',
        addresses,
    };

    if (addresses.length) {
        // console.log('UNSUBSCRIBE from addresses:', addresses);
        sendWhenReady(params);
        subscriptions = [];
        store.state.websocketAccountState = null;
        store.state.websocketTransactions = [];
        store.state.websocketPendingTransactions = [];
        pendingChecks.clear();
    }
}

export function closeWebsocketConnection() {
    stopTxAndStateWebsockets();

    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
    }

    // The socket is only opened for pages that subscribe, so there may be nothing to close
    if (!ws) return;

    // An intentional close must not trigger the auto-reconnect from onclose, and closing a socket
    // that is still connecting fires an error event in Chrome which we do not want to log either
    ws.onclose = null;
    ws.onerror = null;
    stopHeartbeat();
    isOpen = false;
    ws.close();
    ws = null;
}
